import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('留言已提交，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qodl4nlsngrm4lm300', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('留言已成功提交，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mt-2">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              了解福利导航的品牌故事与发展历程
            </p>
          </div>
        </div>

        <section className="max-w-[900px] mx-auto px-4 md:px-6 py-14">
          <h2 className="font-heading text-xl text-foreground-900 mb-6">
            品牌故事
          </h2>
          <div className="text-foreground-700 leading-relaxed space-y-4 text-sm md:text-base">
            {siteInfo.brandStory.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl text-foreground-900 mb-8 text-center">
              信誉与成就
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {siteInfo.reputationHighlights.map((item) => (
                <div
                  key={item.title}
                  className="text-center p-6 rounded-xl bg-background-50 border border-background-200"
                >
                  <div className="text-3xl font-bold text-primary-500 mb-2">
                    {item.value}
                  </div>
                  <div className="font-heading text-base text-foreground-800 mb-1">
                    {item.title}
                  </div>
                  <div className="text-xs text-foreground-500">
                    {item.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[900px] mx-auto px-4 md:px-6 py-14">
          <h2 className="font-heading text-xl text-foreground-900 mb-6">
            核心优势
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {siteInfo.features.map((feature) => (
              <div
                key={feature.title}
                className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200"
              >
                <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base text-foreground-800 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="flex flex-col items-center p-6 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-800 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">{siteInfo.contactInfo.email}</p>
              </div>
              <div className="flex flex-col items-center p-6 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-800 mb-1">客服电话</h3>
                <p className="text-sm text-foreground-500">{siteInfo.contactInfo.phone}</p>
              </div>
              <div className="flex flex-col items-center p-6 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-800 mb-1">微信公众号</h3>
                <p className="text-sm text-foreground-500">{siteInfo.contactInfo.wechat}</p>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-background-50 border border-background-200">
              <h3 className="font-heading text-base text-foreground-800 mb-4 text-center">
                在线留言
              </h3>
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 max-w-lg mx-auto"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="电子邮箱"
                    required
                    className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <input
                  type="text"
                  name="subject"
                  placeholder="主题"
                  className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
                <textarea
                  name="message"
                  placeholder="请输入您的留言内容..."
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                ></textarea>
                <div className="honeypot-wrapper">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                {formStatus !== 'idle' && (
                  <div
                    className={`text-sm p-3 rounded-lg ${
                      formStatus === 'success'
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {formMessage}
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}